/**
 * Self-test suite: Background upload (resumable + bounded retry)
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/AttachmentBackgroundUploadSelfTest.swift`.
 *
 * Test sections:
 *
 *   A. State machine — initial state and basic transitions (11 cases)
 *      A1.  Initial state: new record → queued
 *      A2.  queued + startUpload → uploading (retryCount=0, serverAckedBytes=0)
 *      A3.  uploading + serverAckedProgress → uploading (progress updates)
 *      A4.  uploading + networkLost → waitingNetwork
 *      A5.  waitingNetwork + networkRestored → uploading (resumes from server-acked offset)
 *      A6.  uploading + serverError(503) → retryBackoff (retryCount++)
 *      A7.  retryBackoff tick before deadline → stays retryBackoff
 *      A8.  retryBackoff tick at/after deadline → uploading (resumed)
 *      A9.  6 consecutive server errors → failed (terminal, retry cap exhausted)
 *      A10. uploading + uploadComplete → completed (terminal)
 *      A11. completed + any event → completed (terminal absorption)
 *
 *   B. Policy — backoff schedule + shouldRetry (5 cases)
 *      B1.  Backoff schedule: byte-for-byte match with iOS [2,8,30,120,300,300]s
 *      B2.  shouldRetry: 503 → true (transient server error)
 *      B3.  shouldRetry: 400 → false (permanent client error)
 *      B4.  shouldRetry: 429 → true (rate-limited, transient)
 *      B5.  shouldRetry: "networkError" → false (handled by networkLost path)
 *
 *   C. Type-level server-acked-only guard (1 case)
 *      C1.  transition() accepts no "clientBufferedBytes" input — compile-time
 *           checked by TypeScript (no ClientBufferedBytesEvent in the event union)
 *
 *   D. Store — IndexedDB persistence + bindToMessageId invariant (5 cases)
 *      D1.  upsert + get round-trips the state JSON
 *      D2.  upsert twice (overwrite) returns latest state
 *      D3.  bindToMessageId: first bind succeeds
 *      D4.  bindToMessageId invariant: same messageId is idempotent
 *      D5.  bindToMessageId invariant: different messageId throws
 *      D6.  resetAll clears records and bindings
 *
 * Total: 18 cases.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by Vite `import.meta.glob` in `selftest/index.ts`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  makeQueuedState,
  type AttachmentBackgroundUploadState,
} from "./AttachmentBackgroundUpload";
import { transition, tick } from "./AttachmentBackgroundUploadStateMachine";
import {
  BACKOFF_SCHEDULE_S,
  MAX_RETRY_COUNT,
  shouldRetry,
  backoffDelayMs,
} from "./AttachmentBackgroundUploadPolicy";
import {
  AttachmentBackgroundUploadStore,
  AttachmentBindingConflictError,
} from "./AttachmentBackgroundUploadStore";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

const T0 = 1_000_000; // arbitrary epoch offset — keeps test timestamps legible
const ATTACHMENT_ID = "att-0001";
const TOTAL_BYTES = 1_024_000;

/** Creates a fresh queued state for each test to avoid mutation aliasing. */
function makeQueued(): AttachmentBackgroundUploadState {
  return makeQueuedState(ATTACHMENT_ID, TOTAL_BYTES);
}

// ---------------------------------------------------------------------------
// Test cases
// ---------------------------------------------------------------------------

const cases: TestCase[] = [];

// ── A. State machine ─────────────────────────────────────────────────────────

// A1: Initial state: new record → queued
{
  const state = makeQueued();
  cases.push(
    state.kind === "queued" &&
      state.attachmentId === ATTACHMENT_ID &&
      state.totalBytes === TOTAL_BYTES
      ? pass("A1: makeQueuedState → queued with correct fields")
      : fail(
          "A1: makeQueuedState → queued with correct fields",
          `Got kind=${state.kind}, attachmentId=${state.attachmentId}`,
        ),
  );
}

// A2: queued + startUpload → uploading (retryCount=0, serverAckedBytes=0)
{
  const next = transition(makeQueued(), { kind: "startUpload" }, T0);
  cases.push(
    next.kind === "uploading" &&
      next.serverAckedBytes === 0 &&
      next.retryCount === 0
      ? pass("A2: queued + startUpload → uploading (retryCount=0, serverAckedBytes=0)")
      : fail(
          "A2: queued + startUpload → uploading (retryCount=0, serverAckedBytes=0)",
          `Got kind=${next.kind}`,
        ),
  );
}

// A3: uploading + serverAckedProgress → uploading (progress updates)
{
  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const progressed = transition(
    uploading,
    { kind: "serverAckedProgress", ackedBytes: 512_000 },
    T0 + 1000,
  );
  cases.push(
    progressed.kind === "uploading" &&
      progressed.kind === "uploading" &&
      (progressed as Extract<typeof progressed, { kind: "uploading" }>)
        .serverAckedBytes === 512_000
      ? pass("A3: uploading + serverAckedProgress → uploading with updated serverAckedBytes")
      : fail(
          "A3: uploading + serverAckedProgress → uploading with updated serverAckedBytes",
          `Got kind=${progressed.kind}, serverAckedBytes=${progressed.kind === "uploading" ? progressed.serverAckedBytes : "n/a"}`,
        ),
  );
}

// A4: uploading + networkLost → waitingNetwork
{
  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const progressed = transition(
    uploading,
    { kind: "serverAckedProgress", ackedBytes: 100_000 },
    T0 + 500,
  );
  const waiting = transition(progressed, { kind: "networkLost" }, T0 + 600);
  cases.push(
    waiting.kind === "waitingNetwork" &&
      (waiting as Extract<typeof waiting, { kind: "waitingNetwork" }>)
        .serverAckedBytes === 100_000
      ? pass("A4: uploading + networkLost → waitingNetwork (serverAckedBytes preserved)")
      : fail(
          "A4: uploading + networkLost → waitingNetwork (serverAckedBytes preserved)",
          `Got kind=${waiting.kind}`,
        ),
  );
}

// A5: waitingNetwork + networkRestored → uploading (resumes from server-acked offset)
{
  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const progressed = transition(
    uploading,
    { kind: "serverAckedProgress", ackedBytes: 200_000 },
    T0 + 1000,
  );
  const waiting = transition(progressed, { kind: "networkLost" }, T0 + 2000);
  const resumed = transition(waiting, { kind: "networkRestored" }, T0 + 5000);
  cases.push(
    resumed.kind === "uploading" &&
      (resumed as Extract<typeof resumed, { kind: "uploading" }>)
        .serverAckedBytes === 200_000
      ? pass(
          "A5: waitingNetwork + networkRestored → uploading (resumes from server-acked offset 200_000)",
        )
      : fail(
          "A5: waitingNetwork + networkRestored → uploading (resumes from server-acked offset 200_000)",
          `Got kind=${resumed.kind}, serverAckedBytes=${resumed.kind === "uploading" ? resumed.serverAckedBytes : "n/a"}`,
        ),
  );
}

// A6: uploading + serverError(503) → retryBackoff (retryCount++)
{
  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const backoff = transition(
    uploading,
    { kind: "serverError", errorCode: "503" },
    T0,
  );
  cases.push(
    backoff.kind === "retryBackoff" &&
      (backoff as Extract<typeof backoff, { kind: "retryBackoff" }>).retryCount ===
        1
      ? pass("A6: uploading + serverError(503) → retryBackoff (retryCount=1)")
      : fail(
          "A6: uploading + serverError(503) → retryBackoff (retryCount=1)",
          `Got kind=${backoff.kind}, retryCount=${backoff.kind === "retryBackoff" ? backoff.retryCount : "n/a"}`,
        ),
  );
}

// A7: retryBackoff tick before deadline → stays retryBackoff
{
  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const backoff = transition(
    uploading,
    { kind: "serverError", errorCode: "503" },
    T0,
  );
  if (backoff.kind !== "retryBackoff") {
    cases.push(
      fail("A7: retryBackoff tick before deadline → stays retryBackoff", "Could not reach retryBackoff"),
    );
  } else {
    const beforeDeadline = tick(backoff, backoff.backoffUntilMs - 1);
    cases.push(
      beforeDeadline.kind === "retryBackoff"
        ? pass("A7: retryBackoff tick before deadline → stays retryBackoff")
        : fail(
            "A7: retryBackoff tick before deadline → stays retryBackoff",
            `Got kind=${beforeDeadline.kind}`,
          ),
    );
  }
}

// A8: retryBackoff tick at/after deadline → uploading (resumed)
{
  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const backoff = transition(
    uploading,
    { kind: "serverError", errorCode: "503" },
    T0,
  );
  if (backoff.kind !== "retryBackoff") {
    cases.push(
      fail("A8: retryBackoff tick at deadline → uploading", "Could not reach retryBackoff"),
    );
  } else {
    const afterDeadline = tick(backoff, backoff.backoffUntilMs);
    cases.push(
      afterDeadline.kind === "uploading"
        ? pass("A8: retryBackoff tick at deadline → uploading (resumed)")
        : fail(
            "A8: retryBackoff tick at deadline → uploading (resumed)",
            `Got kind=${afterDeadline.kind}`,
          ),
    );
  }
}

// A9: 6 consecutive server errors → failed (terminal, retry cap exhausted)
{
  let state: AttachmentBackgroundUploadState = makeQueued();
  state = transition(state, { kind: "startUpload" }, T0);
  for (let i = 0; i < MAX_RETRY_COUNT; i++) {
    // Each time: backoff then tick to resume, then error again.
    state = transition(state, { kind: "serverError", errorCode: "503" }, T0 + i * 10_000);
    if (state.kind === "retryBackoff") {
      // Fast-tick to resume
      state = tick(state, state.backoffUntilMs);
    }
  }
  cases.push(
    state.kind === "failed"
      ? pass(`A9: after ${MAX_RETRY_COUNT} retries → failed (terminal)`)
      : fail(
          `A9: after ${MAX_RETRY_COUNT} retries → failed (terminal)`,
          `Got kind=${state.kind}`,
        ),
  );
}

// A10: uploading + uploadComplete → completed (terminal)
{
  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const completed = transition(uploading, { kind: "uploadComplete" }, T0 + 5000);
  cases.push(
    completed.kind === "completed"
      ? pass("A10: uploading + uploadComplete → completed (terminal)")
      : fail(
          "A10: uploading + uploadComplete → completed (terminal)",
          `Got kind=${completed.kind}`,
        ),
  );
}

// A11: completed + any event → completed (terminal absorption)
{
  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const completed = transition(uploading, { kind: "uploadComplete" }, T0);

  const events = [
    { kind: "startUpload" as const },
    { kind: "networkLost" as const },
    { kind: "serverError" as const, errorCode: "503" },
    { kind: "permanentError" as const, errorCode: "400" },
  ] as const;

  let allAbsorbed = true;
  let detail = "";
  for (const evt of events) {
    const next = transition(completed, evt, T0 + 1);
    if (next.kind !== "completed") {
      allAbsorbed = false;
      detail = `completed + "${evt.kind}" → ${next.kind} (should stay completed)`;
      break;
    }
  }
  cases.push(
    allAbsorbed
      ? pass("A11: completed is terminal — all events absorbed")
      : fail("A11: completed is terminal — all events absorbed", detail),
  );
}

// ── B. Policy ────────────────────────────────────────────────────────────────

// B1: Backoff schedule byte-for-byte match with iOS [2,8,30,120,300,300]s
{
  const expectedS = [2, 8, 30, 120, 300, 300];
  const actualS = Array.from(BACKOFF_SCHEDULE_S);
  const match =
    actualS.length === expectedS.length &&
    actualS.every((v, i) => v === expectedS[i]);
  cases.push(
    match
      ? pass(`B1: BACKOFF_SCHEDULE_S = [${expectedS.join(", ")}]s (byte-for-byte iOS match)`)
      : fail(
          "B1: BACKOFF_SCHEDULE_S byte-for-byte iOS match",
          `Expected [${expectedS.join(",")}], got [${actualS.join(",")}]`,
        ),
  );
}

// B2: shouldRetry: 503 → true
{
  cases.push(
    shouldRetry("503")
      ? pass("B2: shouldRetry('503') === true (transient server error)")
      : fail("B2: shouldRetry('503') === true", "Got false"),
  );
}

// B3: shouldRetry: 400 → false
{
  cases.push(
    !shouldRetry("400")
      ? pass("B3: shouldRetry('400') === false (permanent client error)")
      : fail("B3: shouldRetry('400') === false", "Got true"),
  );
}

// B4: shouldRetry: 429 → true (rate-limited, transient)
{
  cases.push(
    shouldRetry("429")
      ? pass("B4: shouldRetry('429') === true (rate-limited, transient)")
      : fail("B4: shouldRetry('429') === true", "Got false"),
  );
}

// B5: shouldRetry: "networkError" → false (handled by networkLost path)
{
  cases.push(
    !shouldRetry("networkError")
      ? pass("B5: shouldRetry('networkError') === false (handled by networkLost path)")
      : fail("B5: shouldRetry('networkError') === false", "Got true"),
  );
}

// ── C. Type-level server-acked-only guard ────────────────────────────────────

// C1: No ClientBufferedBytesEvent exists in the event union.
//     This is a compile-time guarantee enforced by TypeScript's exhaustive
//     discriminated union. We verify it at runtime by confirming that the
//     transition function's event parameter only accepts known kinds.
{
  // If "clientBufferedBytes" existed as an event kind, the discriminated union
  // would route it through the switch. Since it doesn't, TypeScript would flag
  // any attempt to pass `{ kind: "clientBufferedBytes", ... }` as a type error.
  //
  // We verify the positive invariant: the ONLY way to update serverAckedBytes
  // is via serverAckedProgress, and that event requires ackedBytes from the
  // server response. We confirm that a direct "clientBufferedBytes"-style
  // property is absent from the UploadingState type (structural check).

  const uploading = transition(makeQueued(), { kind: "startUpload" }, T0);
  const hasNoClientBufferedField =
    uploading.kind === "uploading" &&
    !("clientBufferedBytes" in uploading) &&
    "serverAckedBytes" in uploading;

  cases.push(
    hasNoClientBufferedField
      ? pass(
          "C1 [CRITICAL]: uploading state has serverAckedBytes but no clientBufferedBytes — server-acked-only invariant enforced at type level",
        )
      : fail(
          "C1 [CRITICAL]: server-acked-only invariant",
          "Unexpected clientBufferedBytes field found on uploading state",
        ),
  );
}

// ── D. Store — persistence + bindToMessageId invariant ───────────────────────

// D1–D6 are async — collected into the `run` function below rather than as
// top-level side effects, so they can use await properly.

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Background upload (resumable + bounded retry)",
  isVerified: true,
  run: async () => {
    // Run the synchronous cases collected above.
    const allCases: TestCase[] = [...cases];

    // ── D. Store ──────────────────────────────────────────────────────────────

    // Each store test uses an isolated DB name to avoid cross-test pollution.
    const dbBase = `kaether:test:att-upload:${Date.now()}`;

    // D1: upsert + get round-trips the state JSON
    try {
      const store = new AttachmentBackgroundUploadStore(`${dbBase}:d1`);
      const stateJson = JSON.stringify(makeQueuedState("att-d1", 1024));
      await store.upsert("att-d1", stateJson);
      const record = await store.get("att-d1");
      allCases.push(
        record !== undefined && record.stateJson === stateJson
          ? pass("D1: upsert + get round-trips stateJson")
          : fail(
              "D1: upsert + get round-trips stateJson",
              `Expected stateJson=${stateJson}, got ${record?.stateJson}`,
            ),
      );
    } catch (err) {
      allCases.push(
        fail("D1: upsert + get round-trips stateJson", String(err)),
      );
    }

    // D2: upsert twice (overwrite) returns latest state
    try {
      const store = new AttachmentBackgroundUploadStore(`${dbBase}:d2`);
      const json1 = JSON.stringify(makeQueuedState("att-d2", 1024));
      const json2 = JSON.stringify({ kind: "uploading", attachmentId: "att-d2", totalBytes: 1024, serverAckedBytes: 512, retryCount: 0 });
      await store.upsert("att-d2", json1);
      await store.upsert("att-d2", json2);
      const record = await store.get("att-d2");
      allCases.push(
        record !== undefined && record.stateJson === json2
          ? pass("D2: second upsert overwrites first (latest state wins)")
          : fail(
              "D2: second upsert overwrites first",
              `Expected json2, got ${record?.stateJson}`,
            ),
      );
    } catch (err) {
      allCases.push(fail("D2: upsert overwrite", String(err)));
    }

    // D3: bindToMessageId: first bind succeeds
    try {
      const store = new AttachmentBackgroundUploadStore(`${dbBase}:d3`);
      const result = await store.bindToMessageId("att-d3", "msg-001");
      allCases.push(
        result.bound === true
          ? pass("D3: bindToMessageId first bind → bound=true")
          : fail("D3: bindToMessageId first bind → bound=true", `Got bound=${result.bound}`),
      );
    } catch (err) {
      allCases.push(fail("D3: bindToMessageId first bind", String(err)));
    }

    // D4: bindToMessageId invariant: same messageId is idempotent
    try {
      const store = new AttachmentBackgroundUploadStore(`${dbBase}:d4`);
      await store.bindToMessageId("att-d4", "msg-001");
      const result2 = await store.bindToMessageId("att-d4", "msg-001");
      allCases.push(
        result2.bound === false
          ? pass("D4: bindToMessageId same messageId → idempotent (bound=false)")
          : fail(
              "D4: bindToMessageId same messageId → idempotent",
              `Expected bound=false, got bound=${result2.bound}`,
            ),
      );
    } catch (err) {
      allCases.push(fail("D4: bindToMessageId idempotent", String(err)));
    }

    // D5: bindToMessageId invariant: different messageId throws
    try {
      const store = new AttachmentBackgroundUploadStore(`${dbBase}:d5`);
      await store.bindToMessageId("att-d5", "msg-001");
      let threw = false;
      let isCorrectError = false;
      try {
        await store.bindToMessageId("att-d5", "msg-002");
      } catch (err) {
        threw = true;
        isCorrectError = err instanceof AttachmentBindingConflictError;
      }
      allCases.push(
        threw && isCorrectError
          ? pass(
              "D5 [INVARIANT]: bindToMessageId with different messageId throws AttachmentBindingConflictError",
            )
          : fail(
              "D5 [INVARIANT]: bindToMessageId different messageId must throw",
              threw ? "Threw but wrong error type" : "Did not throw",
            ),
      );
    } catch (err) {
      allCases.push(
        fail("D5 [INVARIANT]: bindToMessageId conflict", String(err)),
      );
    }

    // D6: resetAll clears records and bindings
    try {
      const store = new AttachmentBackgroundUploadStore(`${dbBase}:d6`);
      await store.upsert("att-d6a", JSON.stringify(makeQueuedState("att-d6a", 512)));
      await store.upsert("att-d6b", JSON.stringify(makeQueuedState("att-d6b", 1024)));
      await store.bindToMessageId("att-d6a", "msg-d6");
      await store.resetAll();
      const records = await store.getAll();
      const binding = await store.getBinding("att-d6a");
      allCases.push(
        records.length === 0 && binding === undefined
          ? pass("D6: resetAll clears all upload records and bindings")
          : fail(
              "D6: resetAll clears all records and bindings",
              `records.length=${records.length}, binding=${binding}`,
            ),
      );
    } catch (err) {
      allCases.push(fail("D6: resetAll", String(err)));
    }

    return buildSuiteResult(allCases);
  },
});
